import {CanActivateFn, Router} from '@angular/router';
import {inject} from '@angular/core';
import {AuthService} from '../../auth/auth.service';
import {ROUTES} from '../../../../routes.enum';
import {OAuthService} from 'angular-oauth2-oidc';
import {of, tap} from 'rxjs';
import {map} from 'rxjs/operators';
import {RuntimeConfigService} from '../../../services/config/runtime-config.service';
import {resolveEmailFromClaims} from '../../soft-launch/soft-launch.util';
import {SOFT_LAUNCH_BLOCK_REASON_KEY, SoftLaunchBlockReason} from '../../soft-launch/soft-launch.constants';

export const azureB2cGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const oAuthService = inject(OAuthService);
  const runtimeConfig = inject(RuntimeConfigService);
  // Get claims (for comparisons ease)
  const needsRegistration = authService.getIdentityClaim(IIdentityClaim.NeedsRegistration, true);
  const registrationPending = authService.getIdentityClaim(IIdentityClaim.RegistrationPending);
  const hasBlockText = authService.getIdentityClaim(IIdentityClaim.BlockText);
  const termsAccepted = authService.getIdentityClaim(IIdentityClaim.TermsAccepted);
  const claims = oAuthService.getIdentityClaims();

  // Prod Soft Launch (AB172621, R2/AC4): restrict login to approved court
  // domains. Checked BEFORE every other branch so a non-approved account is
  // turned away regardless of its registration/terms state — otherwise a
  // pre-existing external B2C account could be routed into a blocked
  // registration flow and strand. Records the block reason so the message page
  // shows the right copy; the marker is cleared on logout.
  if (runtimeConfig.restrictLoginDomains) {
    const email = resolveEmailFromClaims(claims as Record<string, unknown> | null);
    if (!runtimeConfig.isApprovedLoginDomain(email)) {
      sessionStorage.setItem(SOFT_LAUNCH_BLOCK_REASON_KEY, SoftLaunchBlockReason.Login);
      return router.navigate([ROUTES.MESSAGE]);
    }
  }

  // If NeedsRegistration is true, go to registration
  if (needsRegistration === true || needsRegistration === 'True') {
    return router.navigate([ROUTES.REGISTER]);
  }
  // If RegistrationPending is true, go to page that tells them their registration is pending
  else if (registrationPending === true || registrationPending === 'True') {
    return router.navigate([ROUTES.MESSAGE]);
  }
  // If BlockText has value, route to page that shows error
  else if (hasBlockText) {
    return router.navigate([ROUTES.MESSAGE]);
  }
  // Check terms acceptance
  else if (!!claims && claims.hasOwnProperty(IIdentityClaim.TermsAccepted) && (termsAccepted === false || termsAccepted === 'False')) {
    const acceptedTerms = sessionStorage.getItem('termsAccepted');
    if (acceptedTerms === 'true') {
      return true;
    }
    // If terms haven't been accepted (null or 'false'), go to terms page
    return router.navigate([ROUTES.REGISTER, {outlets: {'registeroutlet': 'terms'}}]);
  }
  // If user has valid AgencyUserID, allow access
  else if (authService.getIdentityClaim(IIdentityClaim.AgencyUserID)) {
    return true;
  }
  // Fallback: logout and restart auth flow
  else {
    return of(() => {
      authService.logout();
    }).pipe(
      tap(() => authService.runInitialLoginSequence()),
      map(() => false)
    );
  }
};

export enum IIdentityClaim {
  NeedsRegistration = 'extension_NeedsRegistration',
  RegistrationPending = 'extension_RegistrationPending',
  BlockText = 'extension_BlockText',
  AgencyUserID = 'extension_AgencyUserID',
  TermsAccepted = 'extension_TermsAccepted'
}
